import type { Lead, UserListItem, UsersListResponse } from '@/types';

import React from 'react';
import { Modal, ModalContent, ModalHeader, ModalBody, ModalFooter, Button, Autocomplete, AutocompleteItem } from '@heroui/react';
import { useAtomValue } from 'jotai';
import { toast } from 'react-toastify';

import { apiClient } from '@/utils/api';
import { buildApiParams } from '@/utils/helpers';
import { ErrorHandler } from '@/utils/errorHandler';

interface AssignOwnerModalProps {
  isOpen: boolean;
  onOpenChange: (open: boolean) => void;
  lead: Lead | null;
  isSubmitting: boolean;
  onAssign: (ownerId: number) => void;
}

function getUserLabel(user: UserListItem): string {
  const fullName = `${user.first_name ?? ''} ${user.last_name ?? ''}`.trim();
  return fullName ? `${fullName} (${user.email})` : user.email;
}

export function AssignOwnerModal({ isOpen, onOpenChange, lead, isSubmitting, onAssign }: AssignOwnerModalProps) {
  const [searchTerm, setSearchTerm] = React.useState('');
  const [searchResults, setSearchResults] = React.useState<UserListItem[]>([]);
  const [isSearching, setIsSearching] = React.useState(false);
  const [selectedUserId, setSelectedUserId] = React.useState<string | null>(null);

  const fetchUsers = React.useCallback(
    async (term: string) => {
      if (!term.trim()) {
        setSearchResults([]);
        return;
      }

      setIsSearching(true);
      try {
        const { data } = await apiClient.get('/users', {
          params: buildApiParams({ per_page: 10 }, { search: term.trim() })
        });

        const response = data as UsersListResponse;
        setSearchResults(response.data ?? []);
      } catch (error) {
        ErrorHandler.handle(error);
      } finally {
        setIsSearching(false);
      }
    },
    []
  );

  React.useEffect(() => {
    if (!isOpen) return;

    const handler = window.setTimeout(() => {
      void fetchUsers(searchTerm);
    }, 300);

    return () => {
      window.clearTimeout(handler);
    };
  }, [searchTerm, fetchUsers, isOpen]);

  React.useEffect(() => {
    if (!isOpen) {
      setSearchTerm('');
      setSearchResults([]);
      setSelectedUserId(null);
    }
  }, [isOpen]);

  const handleAssign = React.useCallback(() => {
    if (!selectedUserId) {
      toast.error('Please select a user');
      return;
    }

    onAssign(Number(selectedUserId));
  }, [selectedUserId, onAssign]);

  const handleKeyPress = React.useCallback(
    (e: React.KeyboardEvent) => {
      if (e.key === 'Enter' && !e.shiftKey) {
        e.preventDefault();
        handleAssign();
      }
    },
    [handleAssign]
  );

  if (!lead) return null;

  return (
    <Modal isOpen={isOpen} placement="center" onOpenChange={onOpenChange} size="md" isDismissable={false}>
      <ModalContent>
        {(onClose) => (
          <>
            <ModalHeader className="flex flex-col gap-1">Assign Owner to {lead.domain}</ModalHeader>
            <ModalBody>
              <div className="flex flex-col gap-4" onKeyDown={handleKeyPress}>
                <Autocomplete
                  label="Select Owner"
                  placeholder="Search users by name or email"
                  inputValue={searchTerm}
                  onInputChange={setSearchTerm}
                  selectedKey={selectedUserId}
                  onSelectionChange={(key) => {
                    setSelectedUserId(key as string | null);
                  }}
                  isLoading={isSearching}
                  isClearable
                  onClear={() => {
                    setSelectedUserId(null);
                    setSearchTerm('');
                  }}
                  onOpenChange={() => {}} // Prevent modal from closing
                >
                  {searchResults.map((user) => (
                    <AutocompleteItem key={user.id.toString()} textValue={getUserLabel(user)}>
                      <div className="flex flex-col">
                        <span className="font-medium">{getUserLabel(user)}</span>
                        <span className="text-small text-default-500">{user.email}</span>
                      </div>
                    </AutocompleteItem>
                  ))}
                </Autocomplete>
              </div>
            </ModalBody>
            <ModalFooter>
              <Button color="danger" variant="light" onPress={onClose} isDisabled={isSubmitting}>
                Cancel
              </Button>
              <Button color="primary" onPress={handleAssign} isLoading={isSubmitting}>
                Assign
              </Button>
            </ModalFooter>
          </>
        )}
      </ModalContent>
    </Modal>
  );
}

